/**
 * Markdown do manual (docs/manual) em elementos React: títulos com âncora (no padrão do GitHub, os mesmos links que a
 * documentação usa), parágrafos, listas (com subitens), tabelas, citações, blocos de código, negrito, código, links e
 * imagens (só as do próprio manual, docs/manual/img: imagem de fora não é carregada).
 * Não usa HTML cru (sem dangerouslySetInnerHTML): o texto vira sempre texto, então não há como injetar script.
 */
import { Fragment, type ReactNode } from 'react';

/** Âncora no padrão do GitHub: minúsculas, sem pontuação (acentos ficam), espaços viram hífens. */
export const slug = (heading: string) => heading.trim().toLowerCase()
  .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/[`*_~]/g, '').replace(/<[^>]+>/g, '')
  .replace(/[^\p{L}\p{N}\s-]/gu, '').replace(/\s/g, '-');

/** Texto sem marcação (para busca e para o título). */
export const plain = (md: string) => md.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/[`*]/g, '');

export type Block =
  | { t: 'h'; level: number; text: string; id: string }
  | { t: 'p'; text: string }
  | { t: 'code'; text: string }
  | { t: 'quote'; blocks: Block[] }
  | { t: 'table'; head: string[]; rows: string[][] }
  | { t: 'list'; ordered: boolean; start: number; items: Block[][] }
  | { t: 'hr' }
  | { t: 'img'; alt: string; src: string };

const indentOf = (l: string) => l.length - l.trimStart().length;
const MARKER = /^(\s*)([-*]|\d+\.)\s+(.*)$/;
const isTableSep = (l: string | undefined) => !!l && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(l);
const cells = (l: string) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'));
const startsBlock = (l: string, next?: string) => /^\s*!\[[^\]]*\]\([^)\s]+\)\s*$/.test(l) || /^(#{1,6})\s/.test(l) || /^\s*```/.test(l) || /^\s*>/.test(l)
  || MARKER.test(l) || (/^\s*\|/.test(l) && isTableSep(next)) || /^\s*(---|\*\*\*)\s*$/.test(l);

/** Lê os blocos. `ids` guarda as âncoras já usadas (títulos repetidos ganham -1, -2… como no GitHub). */
export function parseMarkdown(md: string, ids = new Map<string, number>()): Block[] {
  return parseLines(md.replace(/\r\n/g, '\n').split('\n'), ids);
}

function parseLines(lines: string[], ids: Map<string, number>): Block[] {
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) { i++; continue; }
    if (/^\s*```/.test(line)) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i]!)) body.push(lines[i++]!);
      i++;
      out.push({ t: 'code', text: body.join('\n') });
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const base = slug(h[2]!);
      const n = ids.get(base) ?? 0;
      ids.set(base, n + 1);
      out.push({ t: 'h', level: h[1]!.length, text: h[2]!.trim(), id: n ? `${base}-${n}` : base });
      i++;
      continue;
    }
    if (/^\s*(---|\*\*\*)\s*$/.test(line)) { out.push({ t: 'hr' }); i++; continue; }
    const img = /^\s*!\[([^\]]*)\]\(([^)\s]+)\)\s*$/.exec(line);
    if (img) { out.push({ t: 'img', alt: img[1]!, src: img[2]! }); i++; continue; }
    if (/^\s*>/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i]!)) body.push(lines[i++]!.replace(/^\s*>\s?/, ''));
      out.push({ t: 'quote', blocks: parseLines(body, ids) });
      continue;
    }
    if (/^\s*\|/.test(line) && isTableSep(lines[i + 1])) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i]!)) rows.push(cells(lines[i++]!));
      out.push({ t: 'table', head, rows });
      continue;
    }
    const m = MARKER.exec(line);
    if (m) {
      const [block, next] = parseList(lines, i, indentOf(line), ids);
      out.push(block);
      i = next;
      continue;
    }
    const para: string[] = [line.trim()];
    i++;
    while (i < lines.length && lines[i]!.trim() && !startsBlock(lines[i]!, lines[i + 1])) para.push(lines[i++]!.trim());
    out.push({ t: 'p', text: para.join(' ') });
  }
  return out;
}

function parseList(lines: string[], start: number, indent: number, ids: Map<string, number>): [Block, number] {
  let i = start;
  const first = MARKER.exec(lines[i]!)!;
  const ordered = /\d/.test(first[2]!);
  const items: Block[][] = [];
  while (i < lines.length) {
    const m = MARKER.exec(lines[i]!);
    if (!m || m[1]!.length !== indent || /\d/.test(m[2]!) !== ordered) break;
    const contentIndent = indent + m[2]!.length + 1;
    const body: string[] = [m[3]!];
    i++;
    while (i < lines.length) {
      const l = lines[i]!;
      if (!l.trim()) {
        let j = i + 1;
        while (j < lines.length && !lines[j]!.trim()) j++;
        if (j < lines.length && indentOf(lines[j]!) >= contentIndent) { body.push(''); i++; continue; }
        break;
      }
      const ind = indentOf(l);
      if (ind >= contentIndent || (ind > indent && !MARKER.test(l))) { body.push(l.slice(Math.min(contentIndent, ind))); i++; continue; }
      // continuação "preguiçosa" de um parágrafo (sem recuo), como no GitHub
      if (ind <= indent && !startsBlock(l, lines[i + 1]) && body[body.length - 1]?.trim()) { body.push(l.trim()); i++; continue; }
      break;
    }
    items.push(parseLines(body, ids));
  }
  return [{ t: 'list', ordered, start: ordered ? Number(first[2]!.replace('.', '')) : 1, items }, i];
}

/** Como tratar um link: capítulo do manual (com âncora), âncora na página, endereço externo ou só texto. */
export type LinkTarget = { kind: 'chapter'; chapter: string; anchor?: string } | { kind: 'anchor'; anchor: string } | { kind: 'external'; href: string } | { kind: 'text' };
export interface RenderOptions { link: (href: string) => LinkTarget; onNavigate: (target: LinkTarget) => void; highlight?: string;
  /** Endereço da imagem do manual (img/…); sem ele (ou fora da lista), a imagem não aparece. */ image?: (src: string) => string | undefined }

function inline(text: string, o: RenderOptions, key = ''): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /`([^`]+)`|\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${n++}`;
    if (m[1] !== undefined) out.push(<code key={k} className="rounded bg-panel-2 px-1 py-0.5 font-mono text-[0.85em]">{m[1]}</code>);
    else if (m[2] !== undefined) out.push(<strong key={k} className="font-semibold text-fg">{inline(m[2], o, k)}</strong>);
    else {
      const target = o.link(m[4]!);
      const label = inline(m[3]!, o, k);
      if (target.kind === 'external') {
        out.push(<a key={k} href={target.href} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{label}</a>);
      } else if (target.kind === 'text') {
        out.push(<Fragment key={k}>{label}</Fragment>);
      } else {
        out.push(
          <a key={k} href={target.kind === 'anchor' ? `#${target.anchor}` : `#${target.chapter}`} className="text-accent hover:underline"
            onClick={(e) => { e.preventDefault(); o.onNavigate(target); }}>{label}</a>,
        );
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const H_CLASS: Record<number, string> = {
  1: 'mb-3 text-2xl font-semibold text-fg', 2: 'mb-2 mt-7 border-b border-line pb-1 text-lg font-semibold text-fg',
  3: 'mb-2 mt-5 text-base font-semibold text-fg', 4: 'mb-1 mt-4 text-sm font-semibold text-fg', 5: 'mt-3 text-sm font-semibold', 6: 'mt-3 text-sm font-semibold',
};

export function renderBlocks(blocks: Block[], o: RenderOptions, key = 'b', compact = false): ReactNode[] {
  return blocks.map((b, i) => {
    const k = `${key}${i}`;
    switch (b.t) {
      case 'h': {
        const Tag = `h${Math.min(b.level, 6)}` as 'h2';
        return <Tag key={k} id={b.id} className={`scroll-mt-4 ${H_CLASS[b.level]}`}>{inline(b.text, o, k)}</Tag>;
      }
      case 'p':
        return <p key={k} className={compact ? 'leading-relaxed' : 'my-2 leading-relaxed'}>{inline(b.text, o, k)}</p>;
      case 'code':
        return <pre key={k} className="my-3 overflow-x-auto rounded-md border border-line bg-panel-2 p-3 font-mono text-xs leading-relaxed">{b.text}</pre>;
      case 'quote':
        return <blockquote key={k} className="my-3 rounded-r-md border-l-4 border-accent/50 bg-panel-2/60 px-3 py-1 text-[0.95em]">{renderBlocks(b.blocks, o, k)}</blockquote>;
      case 'hr':
        return <hr key={k} className="my-6 border-line" />;
      case 'img': {
        const url = o.image?.(b.src);
        if (!url) return null;
        return (
          <figure key={k} className="my-4">
            <a href={url} target="_blank" rel="noopener noreferrer" title="Abrir a imagem em tamanho real">
              <img src={url} alt={b.alt} loading="lazy" className="w-full rounded-lg border border-line shadow-sm" />
            </a>
            {b.alt ? <figcaption className="mt-1.5 text-center text-xs text-muted">{b.alt}</figcaption> : null}
          </figure>
        );
      }
      case 'table':
        return (
          <div key={k} className="my-3 overflow-x-auto rounded-md border border-line">
            <table className="w-full text-left text-[13px]">
              <thead className="bg-panel-2 text-xs text-muted"><tr>{b.head.map((c, j) => <th key={j} className="px-3 py-2 font-medium">{inline(c, o, `${k}h${j}`)}</th>)}</tr></thead>
              <tbody>{b.rows.map((r, j) => <tr key={j} className="border-t border-line align-top">{r.map((c, l) => <td key={l} className="px-3 py-2">{inline(c, o, `${k}r${j}c${l}`)}</td>)}</tr>)}</tbody>
            </table>
          </div>
        );
      case 'list': {
        const items = b.items.map((it, j) => <li key={j} className="pl-1">{renderBlocks(it, o, `${k}i${j}`, true)}</li>);
        return b.ordered
          ? <ol key={k} start={b.start} className="my-2 ml-5 list-decimal space-y-1 marker:text-muted">{items}</ol>
          : <ul key={k} className="my-2 ml-5 list-disc space-y-1 marker:text-muted">{items}</ul>;
      }
      default:
        return null;
    }
  });
}

/** Markdown simples (relatórios da IA, notas): links externos abrem em nova aba; âncoras rolam a página. */
export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks = parseMarkdown(text);
  return (
    <div className={className}>
      {renderBlocks(blocks, {
        link: (href) => (/^https?:\/\//.test(href) ? { kind: 'external', href } : href.startsWith('#') ? { kind: 'anchor', anchor: href.slice(1) } : { kind: 'text' }),
        onNavigate: (t) => { if (t.kind === 'anchor') document.getElementById(t.anchor)?.scrollIntoView({ behavior: 'smooth' }); },
      })}
    </div>
  );
}
